import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import { GoogleMap, Marker, useLoadScript, Autocomplete } from "@react-google-maps/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faMapMarkerAlt, faSearch } from "@fortawesome/fontawesome-free-solid";
import envValues from "../../enviornment";
import styles from "./MapPinPickerModal.module.scss";

const libraries = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = {
  lat: 40.7128, // New York City default
  lng: -74.006,
};

const MapPinPickerModal = ({
  isOpen,
  onClose,
  onConfirm,
  initialCoordinates,
  initialAddress,
  title,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: envValues.GOOGLE_API_KEY,
    libraries,
  });

  const [markerPosition, setMarkerPosition] = useState(
    initialCoordinates || defaultCenter
  );
  const [mapCenter, setMapCenter] = useState(
    initialCoordinates || defaultCenter
  );
  const [selectedAddress, setSelectedAddress] = useState(initialAddress || "");
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  // Update position when initialCoordinates changes
  useEffect(() => {
    if (initialCoordinates) {
      setMarkerPosition(initialCoordinates);
      setMapCenter(initialCoordinates);
    }
    if (initialAddress) {
      setSelectedAddress(initialAddress);
    }
  }, [initialCoordinates, initialAddress]);

  // Fetch address from coordinates (reverse geocoding)
  const fetchAddressFromCoords = useCallback(async (lat, lng) => {
    setIsLoadingAddress(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const latlng = { lat, lng };

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === "OK" && results[0]) {
          setSelectedAddress(results[0].formatted_address);
        } else {
          setSelectedAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        }
        setIsLoadingAddress(false);
      });
    } catch (error) {
      console.error("Error fetching address:", error);
      setSelectedAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      setIsLoadingAddress(false);
    }
  }, []);

  const handleMarkerDragEnd = useCallback(
    (e) => {
      const newLat = e.latLng.lat();
      const newLng = e.latLng.lng();
      const newPosition = { lat: newLat, lng: newLng };
      
      setMarkerPosition(newPosition);
      fetchAddressFromCoords(newLat, newLng);
    },
    [fetchAddressFromCoords]
  );

  const handleMapClick = useCallback(
    (e) => {
      const newLat = e.latLng.lat();
      const newLng = e.latLng.lng();
      const newPosition = { lat: newLat, lng: newLng };
      
      setMarkerPosition(newPosition);
      fetchAddressFromCoords(newLat, newLng);
    },
    [fetchAddressFromCoords]
  );

  const handleConfirm = () => {
    onConfirm({
      coordinates: markerPosition,
      address: selectedAddress,
    });
  };

  const handleClose = () => {
    // Reset to initial values on close
    setMarkerPosition(initialCoordinates || defaultCenter);
    setMapCenter(initialCoordinates || defaultCenter);
    setSelectedAddress(initialAddress || "");
    onClose();
  };

  const handleRecenter = () => {
    setMapCenter(markerPosition);
  };

  const onLoadAutocomplete = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      
      if (place.geometry) {
        const newPosition = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        
        setMarkerPosition(newPosition);
        setMapCenter(newPosition);
        setSelectedAddress(place.formatted_address || place.name || "");
        setSearchValue("");
      }
    }
  };

  if (loadError) {
    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={handleClose}
        className={styles.modal}
        overlayClassName={styles.overlay}
        ariaHideApp={false}
      >
        <div className={styles.errorContainer}>
          <h2>Error Loading Map</h2>
          <p>
            Failed to load Google Maps. Please check your internet connection
            and try again.
          </p>
          <button className={styles.closeButton} onClick={handleClose}>
            Close
          </button>
        </div>
      </Modal>
    );
  }

  if (!isLoaded) {
    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={handleClose}
        className={styles.modal}
        overlayClassName={styles.overlay}
        ariaHideApp={false}
      >
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p>Loading map...</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
      ariaHideApp={false}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.icon} />
            {title || "Set Cafe Location"}
          </h2>
          <button
            className={styles.closeIcon}
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.searchAndButtonsContainer}>
          <div className={styles.searchBoxWrapper}>
            <Autocomplete
              onLoad={onLoadAutocomplete}
              onPlaceChanged={onPlaceChanged}
            >
              <input
                type="text"
                placeholder="Search location (e.g., JFK Airport)..."
                className={styles.searchInput}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </Autocomplete>
            <FontAwesomeIcon
              icon={faSearch}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#999",
                pointerEvents: "none",
              }}
            />
          </div>
          <div className={styles.actionButtons}>
            <button
              className={styles.cancelButton}
              onClick={handleClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className={styles.confirmButton}
              onClick={handleConfirm}
              type="button"
              disabled={isLoadingAddress}
            >
              ✓ Confirm
            </button>
          </div>
        </div>

        <div className={styles.mapContainer}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={17}
            center={mapCenter}
            onClick={handleMapClick}
            options={{
              streetViewControl: false,
              mapTypeControl: true,
              fullscreenControl: false,
              zoomControl: true,
            }}
          >
            <Marker
              position={markerPosition}
              draggable={true}
              onDragEnd={handleMarkerDragEnd}
              animation={window.google.maps.Animation.DROP}
            />
          </GoogleMap>
        </div>

        <div className={styles.infoSection}>
          {isLoadingAddress ? (
            <p className={styles.loadingText}>📍 Loading address...</p>
          ) : selectedAddress ? (
            <p className={styles.selectedAddressText}>
              📍 {selectedAddress}
            </p>
          ) : (
            <p className={styles.selectedAddressText}>
              📍 Click on the map or search to select a location
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

MapPinPickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  initialCoordinates: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  initialAddress: PropTypes.string,
  title: PropTypes.string,
};

MapPinPickerModal.defaultProps = {
  initialCoordinates: null,
  initialAddress: "",
  title: "Set Café Location",
};

export default MapPinPickerModal;

